/** @jsx jsx */
import type { CSSObject } from '@emotion/react';
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import classNames from 'classnames';

import config from 'js/app/config';
import type { InjectedRouter } from 'js/lib/connectToRouter';
import redirect from 'js/lib/coursera.redirect';
import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import { Button, Typography, breakpoints } from '@coursera/cds-core';
import type { ButtonName, SectionName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { TrackedA } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/video-logged-out-page';

type InputProps = {
  productLink: string;
  buttonClass?: string;
  customMessage?: React.ReactNode;
  // Adding '&&' to css rules is currently the only neat way to override CDS default styles for special cases
  buttonCss?: Record<'&&', CSSObject>;
  buttonName: ButtonName;
  sectionName: SectionName;
};

export type Props = InputProps;

const styles = {
  ctaContainer: css`
    text-align: center;
    align-items: center;
    justify-content: center;
    margin: 0 0 40px 0;
    color: var(--cds-color-neutral-background-primary);

    .try-course-btn {
      ${breakpoints.down('sm')} {
        width: 100%;
      }
    }
  `,
};
type ClickEventProps = {
  e: React.MouseEvent;
  redirectToUrl: string;
  router: InjectedRouter;
};

const handleCTAClick = ({ e, redirectToUrl, router }: ClickEventProps) => {
  e.preventDefault();
  if (user.isAuthenticatedUser()) {
    redirect.setLocation(redirectToUrl);
  } else {
    router.replace({
      pathname: router.location.pathname,
      query: {
        ...router.location.query,
        authMode: 'signup',
        redirectTo: redirectToUrl,
      },
    });
  }
};

const TryCourseButton: React.FC<Props> = ({
  buttonCss,
  productLink,
  buttonClass,
  customMessage,
  buttonName,
  sectionName,
}) => {
  const router = useRouter();
  const trackEvent = useTracker();

  const productEnrollUrl = new URL(productLink, config.url.base);
  productEnrollUrl.searchParams.append('action', 'enroll');
  const productEnrollRelativeUrl = `${productEnrollUrl.pathname}${productEnrollUrl.search}`;

  const handleClick = (e: React.MouseEvent) => {
    trackEvent('click_button', { button: { name: buttonName }, pageSection: { sectionName } });
    handleCTAClick({ e, redirectToUrl: productEnrollRelativeUrl, router });
  };

  return (
    <div className="cta-container-cds" css={styles.ctaContainer}>
      <Button
        component={TrackedA}
        // Since CSR takes a second for the onclick handler to work, default to using href
        href={user.isAuthenticatedUser() ? productEnrollRelativeUrl : '?authMode=signup'}
        withVisibilityTracking
        trackingName="vlp_try_course_button"
        className={classNames(buttonClass, 'try-course-btn')}
        onClick={(e: React.MouseEvent) => handleClick(e)}
        data-e2e="try_course_button"
        data-catchpoint="vlp-try-course-button"
        css={buttonCss}
      >
        {customMessage || (
          <Typography
            component={FormattedMessage}
            message={_t('Try the Course for {free}')}
            free={
              <span className="bold-text" css={{ '&&': { fontWeight: 'bold' } }}>
                {_t('Free')}
              </span>
            }
          />
        )}
      </Button>
    </div>
  );
};

export default TryCourseButton;
